import { logEmailAttempt, sendReportEmail } from '../../services/cloudService';
import { describeError } from '../../services/errorMessage';
import { newSendKey } from '../../services/pendingSendPersistence';
import {
    markiereGestelltJeFrist,
    markiereHinweisVersendet,
    setzeFrageEmpfaengerWoLeer,
} from '../../services/planungsphaseService';
import { legeUnterlageAb, type AblageErgebnis } from '../../services/unterlagenAblage';
import type { EmpfaengerZuordnung } from './empfaenger';
import { gruppiereNachFrist, stellMeldung, type FristErgebnis } from './fristGruppen';
import { fristDatum, fristTageFuerCode } from './planungLogic';
import type { FrageAdressat, Planungsfrage, Planungshinweis } from './types';
import { datumDeutsch } from './unterlagenLogic';

/**
 * DER eine Versandweg der Planungsphase (Plan Stufe 4.1): Klaerungsliste je
 * Adressat, Unterlagenanforderung je Lieferant, Bedenkenanzeige. Hier steht
 * die Reihenfolge, die die Beweiskette traegt — und nur sie:
 *
 *   PDF → Ablage (`legeUnterlageAb`, Modul `planungsphase`, Anlass `versand`)
 *       → E-Mail JE EMPFAENGER mit dem PDF als Base64 (`sendReportEmail`)
 *       → Sendebericht je Mail (`email_logs` ueber `logEmailAttempt`)
 *       → Ergebnis mit dem Zeitpunkt des ERSTEN Erfolgs.
 *
 * ═══ WARUM BASE64 UND NICHT DIE ABGELEGTE DATEI ═════════════════════════
 * `api/send-email.ts` holt ein `pdfUrl` nur vom eigenen Supabase-Host, und
 * der Bucket der Ablage ist privat — eine signierte URL waere ein zweiter
 * Weg mit eigener Ablaufzeit. Die Bytes liegen ohnehin im Browser.
 *
 * ═══ `email_logs` ENTSTEHT NICHT IN `sendReportEmail` ════════════════════
 * Geprueft: der Versandaufruf schreibt keinen Sendebericht; das tun seine
 * Aufrufer (EmailModal, zertifikatClient). Deshalb hier je Empfaenger ein
 * eigener Eintrag — Erfolg wie Fehler —, damit der Reiter „Uebergabe" spaeter
 * aus EINER Quelle liest, was hinausging (Plan 6.3). Ein gescheiterter
 * Eintrag blockiert nichts, steht aber im Ergebnis (`hinweise`).
 *
 * ═══ STEMPELT NICHT ══════════════════════════════════════════════════════
 * `versende` setzt keinen Status. Erst der Mail-ERFOLG stempelt, und WAS
 * gestempelt wird, haengt an der Art: Fragen `gestellt` mit Katalogfrist,
 * Unterlage `angefordert`, Hinweis `versendet_am`. Das tun die Orchestrierer
 * darunter (`versendeKlaerungsliste`, `versendeBedenkenanzeige`), jeder nach
 * dem Muster „Mail raus → stempeln → Stempelfehler SICHTBAR, nie still".
 * Der Guard „erstes Datum bleibt" steht in den UPDATEs des Service.
 *
 * ═══ ABLAGE SCHEITERT → MAIL GEHT TROTZDEM ═══════════════════════════════
 * Die Ablage ist der Anker fuer „was ging raus", aber sie darf den Versand
 * nicht verhindern — der Bauherr wartet auf das Blatt, nicht auf unsere
 * Ordnung. Das Ergebnis traegt den Ablagefehler sichtbar, der Dialog zeigt
 * ihn, und die Fassung laesst sich spaeter nachlegen.
 *
 * ═══ MAILGRENZE ═══════════════════════════════════════════════════════════
 * Microsoft erlaubt dem Absenderkonto etwa 30 Mails je Minute, die API
 * deckelt bei 300 je 15 Minuten. Die Empfaenger werden deshalb nacheinander
 * bedient, mit einer Pause zwischen zwei Mails — nicht parallel.
 *
 * ═══ WIRFT NIE ═══════════════════════════════════════════════════════════
 * Alles kommt als Ergebnis zurueck. Ein Wurf mitten im Lauf liesse offen, ob
 * die erste Mail schon hinausging — genau die Luecke, aus der ein zweiter
 * Versand entsteht.
 */

export type VersandArt = 'klaerungsliste' | 'unterlagenanforderung' | 'bedenkenanzeige';

export interface VersandEmpfaenger {
    name: string;
    email: string;
}

export interface VersandAuftrag {
    art: VersandArt;
    projectId: string;
    planungId: string;
    /** Fuer den Sendebericht (`email_logs.project_name`). */
    projektName?: string;
    /** Aus der Empfaenger-Zuordnung, im Dialog aenderbar. */
    empfaenger: VersandEmpfaenger[];
    /** Vorbelegt, im Dialog aenderbar. */
    betreff: string;
    text: string;
    pdf: Blob;
    /** Mit Endung; zugleich Name des Anhangs. */
    dateiname: string;
    /** Anzeigename in der Ablage. */
    titel: string;
    /** Stabile Kennung der Fassung (Doppelablage-Schutz). */
    fassungRef: string;
    /** „Stand 17.10.2026" — Chip in der Ablage. */
    stand?: string;
    /**
     * Warum NICHT versendet werden darf — oder `null`. Pflichtfeld, damit
     * kein Aufrufer die Frage vergisst: der Riegel gilt zentral fuer alle drei
     * Arten (Codex-Review Stufe 4, P1a). Heute der Uebungsprojekt-Riegel wie
     * im Berichtsversand (`sendGuard.UEBUNG_BLOCK`): aus einem Uebungsprojekt
     * geht keine Mail an Externe, und der Sendebericht wiese die Zeile
     * ohnehin ab (Trigger Nr. 101). `versende` verweigert mit diesem Text
     * VOR Ablage und Mail.
     */
    sperrgrund: string | null;
}

export interface VersandErgebnis {
    /** `versendet` = an alle, `teilweise` = an einen Teil, `fehler` = an niemanden. */
    status: 'versendet' | 'teilweise' | 'fehler';
    versendetAn: string[];
    /** Je nicht erreichter Empfaenger ein Satz. */
    fehler: string[];
    /** `null`, wenn gar nicht erst abgelegt wurde (kein gueltiger Empfaenger, Sperre). */
    ablage: AblageErgebnis | null;
    /**
     * Zeitpunkt der Zustellung an den HAUPTEMPFAENGER — Grundlage fuer jeden
     * Stempel. `null`, wenn er nicht erreicht wurde, auch wenn Kopien
     * hinausgingen: eine Bedenkenanzeige, die nur den Architekten erreicht
     * hat, deckt keine Frage (Codex-Review Stufe 4, P1b).
     */
    versendetAm: string | null;
    /** Der erste Eintrag der Liste — an ihn ist das Blatt gerichtet. */
    hauptempfaenger: VersandEmpfaenger | null;
    /** Hat die Mail an den Hauptempfaenger den Server verlassen? Nur dann wird gestempelt. */
    hauptempfaengerErreicht: boolean;
    /** Nicht-fatale Hinweise: Ablage gescheitert, Sendebericht nicht geschrieben. */
    hinweise: string[];
}

export interface VersandOptionen {
    /** Pause zwischen zwei Empfaengern; Tests reichen eine Attrappe herein. */
    warte?: (ms: number) => Promise<void>;
    jetzt?: () => Date;
}

/** Zwei Sekunden zwischen zwei Mails — unter der Microsoft-Grenze von ~30/min. */
export const PAUSE_ZWISCHEN_EMPFAENGERN_MS = 2000;

const E_MAIL_MUSTER = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const istGueltigeEmail = (s: string | null | undefined): boolean => E_MAIL_MUSTER.test((s || '').trim());

const sauber = (s: string | null | undefined): string => (s || '').replace(/\s+/g, ' ').trim();

/**
 * Was an der Empfaengerliste des Dialogs nicht stimmt — oder `null`. Anders
 * als in der Zuordnung (`pruefeEmpfaengerEingabe`) ist die E-Mail hier
 * PFLICHT: ohne Adresse gibt es nichts zu versenden.
 */
export const pruefeVersandEmpfaenger = (liste: ReadonlyArray<VersandEmpfaenger>): string | null => {
    if (!liste || !liste.length) return 'Mindestens ein Empfänger mit E-Mail-Adresse ist nötig.';
    const gesehen = new Set<string>();
    for (const e of liste) {
        const name = sauber(e?.name);
        const email = sauber(e?.email);
        if (!name) return 'Jeder Empfänger braucht einen Namen.';
        if (!istGueltigeEmail(email)) return `Die E-Mail-Adresse von „${name}" ist nicht vollständig.`;
        const schluessel = email.toLowerCase();
        if (gesehen.has(schluessel)) return `${email} steht doppelt in der Liste.`;
        gesehen.add(schluessel);
    }
    return null;
};

/**
 * Vorbelegung des Dialogs aus der Zuordnung „Adressat → Person": die Person
 * des Adressaten, sofern sie eine E-Mail hat. Fehlt sie oder die Adresse,
 * sagt `hinweis`, was zu tun ist — eingetragen wird im Dialog, gespeichert
 * wird die Zuordnung dadurch NICHT (das bleibt ein eigener Klick oben in
 * der Fragenliste).
 */
export const empfaengerVorgabeFuer = (
    zuordnung: EmpfaengerZuordnung | null | undefined,
    adressat: FrageAdressat,
    adressatLabel: string,
    lesehinweis: string | null = null,
): { vorgabe: VersandEmpfaenger[]; hinweis: string | null } => {
    if (lesehinweis) return { vorgabe: [], hinweis: lesehinweis };
    const person = zuordnung ? zuordnung[adressat] : undefined;
    if (!person || !sauber(person.name)) {
        return {
            vorgabe: [],
            hinweis: `Für „${adressatLabel}" ist noch kein Empfänger zugeordnet — bitte Name und E-Mail eintragen. `
                + 'Dauerhaft merkt sich das die Zuordnung oben in der Fragenliste.',
        };
    }
    const email = sauber(person.email);
    if (!istGueltigeEmail(email)) {
        return {
            vorgabe: [{ name: sauber(person.name), email }],
            hinweis: `Für ${sauber(person.name)} ist keine E-Mail-Adresse hinterlegt — bitte ergänzen.`,
        };
    }
    return { vorgabe: [{ name: sauber(person.name), email }], hinweis: null };
};

// ═══════════════════════════════════════════════════════════════════════════
// MAILTEXT
// ═══════════════════════════════════════════════════════════════════════════

const escapeHtml = (s: string): string =>
    s.replace(/[&<>"']/g, z => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[z] || z));

/**
 * Der Klartext als HTML-Mail, Absatz je Zeile (Muster `zertifikatMailText`).
 * Der Nutzertext wird maskiert — er kommt aus einem Eingabefeld und darf in
 * der Mail kein Markup werden.
 */
export const mailHtmlAusText = (text: string): string => {
    const zeilen = (text || '').replace(/\r\n/g, '\n').split('\n');
    const koerper = zeilen
        .map(z => (z.trim() ? `<p style="margin:0 0 4px 0">${escapeHtml(z)}</p>` : '<br>'))
        .join('');
    return `<div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #334155; line-height: 1.6; max-width: 600px;">${koerper}</div>`;
};

export interface KlaerungslisteMailVorlage {
    betreff: string;
    text: string;
}

const fragenWort = (n: number): string => (n === 1 ? 'Frage' : 'Fragen');

/**
 * Betreff und Anschreiben der Klaerungsliste — vorbelegt, im Dialog
 * aenderbar. Die Fristen stehen im Text, damit der Empfaenger sie liest,
 * ohne das PDF zu oeffnen: je Fristgruppe ein Datum ab HEUTE (der Stempel
 * nach dem Versand rechnet dieselbe Frist ab dem Stelltag). Bereits
 * gestellte Fragen bekommen keine neue Frist — das ist eine Erinnerung.
 */
export const klaerungslisteMailVorlage = (v: {
    projektName?: string | null;
    projektNummer?: string | null;
    adressatLabel: string;
    fragen: ReadonlyArray<Pick<Planungsfrage, 'id' | 'code' | 'gestellt_am'>>;
    heuteIso: string;
    absender?: string | null;
}): KlaerungslisteMailVorlage => {
    const projekt = sauber(v.projektName) || 'Bauvorhaben';
    const nummer = sauber(v.projektNummer);
    const projektText = nummer ? `„${projekt}" (Nr. ${nummer})` : `„${projekt}"`;
    const fragen = v.fragen || [];
    const neue = fragen.filter(f => !f.gestellt_am);
    const erinnerte = fragen.length - neue.length;
    const gruppen = gruppiereNachFrist(neue, fristTageFuerCode);

    const fristen = gruppen
        .map(g => `bis ${datumDeutsch(fristDatum(v.heuteIso, g.tage)) || 'zum genannten Termin'} (${g.ids.length} ${fragenWort(g.ids.length)})`)
        .join(' und ');

    const zeilen: string[] = [
        'Sehr geehrte Damen und Herren,',
        '',
        `für das Bauvorhaben ${projektText} bitten wir Sie als ${sauber(v.adressatLabel) || 'Beteiligten'} um Beantwortung `
            + `der beigefügten Klärungsliste der Planungsphase (${fragen.length} ${fragenWort(fragen.length)}).`,
        '',
    ];
    if (neue.length) zeilen.push(`Wir bitten um Rückantwort ${fristen}.`);
    if (erinnerte) {
        zeilen.push(neue.length
            ? `${erinnerte} ${fragenWort(erinnerte)} davon wurde${erinnerte === 1 ? '' : 'n'} Ihnen bereits gestellt — wir erinnern an die noch offene Beantwortung.`
            : 'Diese Fragen wurden Ihnen bereits gestellt — wir erinnern an die noch offene Beantwortung.');
    }
    zeilen.push(
        '',
        'Bitte antworten Sie je Frage unter Angabe des Fragencodes (z. B. PL-A-01), gern direkt auf dem Blatt oder per E-Mail.',
        '',
        'Mit freundlichen Grüßen',
    );
    const absender = sauber(v.absender);
    if (absender) zeilen.push(absender);

    const betreffTeile = ['Klärungsliste Planungsphase', nummer ? `${projekt} (${nummer})` : projekt, sauber(v.adressatLabel)];
    return { betreff: betreffTeile.filter(Boolean).join(' – '), text: zeilen.join('\n') };
};

// ═══════════════════════════════════════════════════════════════════════════
// VERSAND
// ═══════════════════════════════════════════════════════════════════════════

/** Blob → Base64 ohne `data:`-Praefix, in Bloecken — ein PDF mit Logo sprengt
 *  `String.fromCharCode(...alleBytes)` auf einmal. Laeuft im Browser wie in
 *  Node (Tests). */
const blobAlsBase64 = async (blob: Blob): Promise<string> => {
    const bytes = new Uint8Array(await blob.arrayBuffer());
    const BLOCK = 0x8000;
    let binaer = '';
    for (let i = 0; i < bytes.length; i += BLOCK) {
        binaer += String.fromCharCode(...bytes.subarray(i, i + BLOCK));
    }
    return btoa(binaer);
};

const standardWarte = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Der Satz zur Ablage, der in `hinweise` wandert — oder `null`, wenn es
 * nichts zu melden gibt. Exportiert, damit der Dialog denselben Satz NICHT
 * doppelt zeigt (einmal am Ablagekasten, einmal unter den Hinweisen).
 */
export const ablageHinweis = (ablage: AblageErgebnis | null): string | null => {
    if (!ablage) return null;
    if (ablage.status === 'fehler') return `Die Fassung konnte nicht in der Ablage gesichert werden: ${ablage.fehler}`;
    // `schon_abgelegt` mit liegengebliebener Datei — abgelegt ist sie, aber
    // etwas gehoert gemeldet.
    return ablage.fehler || null;
};

/** Doppelte Adressen fallen weg, ungueltige landen als Satz in `fehler`. */
const bereinigeEmpfaenger = (
    liste: ReadonlyArray<VersandEmpfaenger> | null | undefined,
    fehler: string[],
): VersandEmpfaenger[] => {
    const gueltige: VersandEmpfaenger[] = [];
    const gesehen = new Set<string>();
    for (const e of liste || []) {
        const email = sauber(e?.email);
        const name = sauber(e?.name) || email;
        if (!istGueltigeEmail(email)) {
            fehler.push(`${name || 'Empfänger ohne Namen'}: keine gültige E-Mail-Adresse — nicht versendet.`);
            continue;
        }
        const schluessel = email.toLowerCase();
        if (gesehen.has(schluessel)) continue;
        gesehen.add(schluessel);
        gueltige.push({ name, email });
    }
    return gueltige;
};

const gleicheAdresse = (a: string | null | undefined, b: string | null | undefined): boolean =>
    sauber(a).toLowerCase() === sauber(b).toLowerCase();

/**
 * Der Hauptempfaenger einer Liste: ihr ERSTER Eintrag, bereinigt — der Bauherr
 * bei der Bedenkenanzeige, der Adressat der Klaerungsliste; alles danach
 * sind Kopien. `null` bei leerer Liste oder leerem Eintrag.
 */
export const hauptempfaengerVon = (liste: ReadonlyArray<VersandEmpfaenger> | null | undefined): VersandEmpfaenger | null => {
    const erster = liste && liste.length ? liste[0] : null;
    if (!erster) return null;
    const email = sauber(erster.email);
    const name = sauber(erster.name) || email;
    return name || email ? { name, email } : null;
};

/**
 * Versendet eine Fassung: Sperre pruefen → Ablage → Mail je Empfaenger →
 * Sendebericht je Mail. Stempelt NICHTS (siehe Dateikopf). Wirft nie.
 */
export const versende = async (a: VersandAuftrag, optionen: VersandOptionen = {}): Promise<VersandErgebnis> => {
    const ergebnis: VersandErgebnis = {
        status: 'fehler', versendetAn: [], fehler: [], ablage: null, versendetAm: null,
        hauptempfaenger: null, hauptempfaengerErreicht: false, hinweise: [],
    };
    try {
        const warte = optionen.warte || standardWarte;
        const jetzt = optionen.jetzt || (() => new Date());
        const haupt = hauptempfaengerVon(a.empfaenger);
        ergebnis.hauptempfaenger = haupt;

        // Der Riegel steht VOR allem anderen: weder Ablage noch Mail noch
        // Sendebericht — nur der Grund, sichtbar.
        const sperre = sauber(a.sperrgrund);
        if (sperre) {
            ergebnis.fehler.push(sperre);
            return ergebnis;
        }

        const empfaenger = bereinigeEmpfaenger(a.empfaenger, ergebnis.fehler);
        if (!empfaenger.length) {
            if (!ergebnis.fehler.length) ergebnis.fehler.push('Kein Empfänger — es wurde nichts versendet.');
            return ergebnis;
        }

        // Zuerst die Ablage: sie ist der Anker fuer „was ging raus". Scheitert
        // sie, geht die Mail trotzdem — der Fehler steht im Ergebnis.
        ergebnis.ablage = await legeUnterlageAb({
            projectId: a.projectId,
            modul: 'planungsphase',
            datei: a.pdf,
            dateiname: a.dateiname,
            titel: a.titel,
            stand: a.stand,
            fassungRef: a.fassungRef,
            anlass: 'versand',
        });
        const ablageSatz = ablageHinweis(ergebnis.ablage);
        if (ablageSatz) ergebnis.hinweise.push(ablageSatz);

        const pdfBase64 = await blobAlsBase64(a.pdf);
        const html = mailHtmlAusText(a.text);
        const pdfPfad = ergebnis.ablage.dokument ? ergebnis.ablage.dokument.storage_pfad : undefined;

        for (let i = 0; i < empfaenger.length; i++) {
            const e = empfaenger[i];
            if (i > 0) await warte(PAUSE_ZWISCHEN_EMPFAENGERN_MS);

            let fehlerText: string | null = null;
            try {
                await sendReportEmail({
                    recipients: [e.email],
                    subject: a.betreff,
                    message: a.text,
                    html,
                    pdfBase64,
                    filename: a.dateiname,
                });
                ergebnis.versendetAn.push(e.email);
                // Nur die Mail an den Hauptempfaenger traegt den Zeitpunkt, an
                // dem spaeter gestempelt wird — eine Kopie beweist keinen Zugang.
                if (haupt && gleicheAdresse(e.email, haupt.email)) {
                    ergebnis.hauptempfaengerErreicht = true;
                    ergebnis.versendetAm = jetzt().toISOString();
                }
            } catch (err) {
                fehlerText = describeError(err);
                ergebnis.fehler.push(`${e.name} <${e.email}>: ${fehlerText}`);
            }

            // Der Sendebericht gehoert zur Beweiskette; sein Scheitern darf
            // aber weder den naechsten Empfaenger aufhalten noch wie ein
            // Fehlschlag des Versands aussehen.
            try {
                await logEmailAttempt({
                    project_id: a.projectId,
                    project_name: sauber(a.projektName),
                    recipients: e.email,
                    subject: a.betreff,
                    status: fehlerText ? 'error' : 'success',
                    error_message: fehlerText || undefined,
                    body_html: html,
                    pdf_url: pdfPfad || undefined,
                    send_key: newSendKey(),
                });
            } catch (err) {
                ergebnis.hinweise.push(`Der Sendebericht für ${e.email} konnte nicht gespeichert werden: ${describeError(err)}`);
            }
        }

        ergebnis.status = ergebnis.versendetAn.length === empfaenger.length
            ? 'versendet'
            : ergebnis.versendetAn.length ? 'teilweise' : 'fehler';
        return ergebnis;
    } catch (err) {
        ergebnis.fehler.push(`Der Versand wurde abgebrochen: ${describeError(err)}`);
        ergebnis.status = ergebnis.versendetAn.length ? 'teilweise' : 'fehler';
        return ergebnis;
    }
};

/**
 * Der Satz, wenn Kopien hinausgingen, der Hauptempfaenger aber nicht erreicht
 * wurde — dann wird NICHTS gestempelt, und das muss so dastehen, nicht als
 * halber Erfolg. Leer (`null`), wenn der Hauptempfaenger erreicht wurde oder
 * gar nichts hinausging (dann sagt es der Versandstatus selbst).
 */
export const nichtZugestelltMeldung = (v: VersandErgebnis): string | null => {
    if (v.hauptempfaengerErreicht || !v.versendetAn.length) return null;
    const wer = v.hauptempfaenger
        ? `${v.hauptempfaenger.name}${v.hauptempfaenger.email ? ` <${v.hauptempfaenger.email}>` : ''}`
        : 'den Hauptempfänger';
    return `An den Hauptempfänger ${wer} nicht zugestellt — nichts gestempelt. `
        + `Erreicht wurden nur Kopien: ${v.versendetAn.join(', ')}.`;
};

/** Stempeln darf nur, wer den Hauptempfaenger erreicht hat — mit dessen Zeitpunkt. */
const darfStempeln = (v: VersandErgebnis): v is VersandErgebnis & { versendetAm: string; hauptempfaenger: VersandEmpfaenger } =>
    v.hauptempfaengerErreicht && !!v.versendetAm && !!v.hauptempfaenger;

// ═══════════════════════════════════════════════════════════════════════════
// KLAERUNGSLISTE — Mail raus → Fragen gestellt (Frist je Code) → Empfaenger
// ═══════════════════════════════════════════════════════════════════════════

export interface KlaerungslisteStempel {
    /** Je Fristgruppe, was gestempelt wurde; leer, wenn alle schon gestellt waren. */
    fristen: FristErgebnis[];
    /** Zeilen, die jetzt den Empfaenger tragen (nur, wo noch keiner stand). */
    empfaengerGesetzt: number;
    /** Klartext, wenn der Stempel scheiterte — die Mail ist RAUS, der Stempel fehlt. */
    fehler: string | null;
}

export interface KlaerungslisteStempelAuftrag {
    fragen: ReadonlyArray<Pick<Planungsfrage, 'id' | 'code' | 'gestellt_am'>>;
    /** Zeitpunkt der Zustellung an den Hauptempfaenger (`VersandErgebnis.versendetAm`). */
    versendetAm: string;
    /** Der Hauptempfaenger (`hauptempfaengerVon`) — er wird an den Fragen festgehalten. */
    empfaenger: VersandEmpfaenger;
}

/**
 * Stempelt nach einem erfolgreichen Versand — auch zum NACHHOLEN, wenn der
 * erste Anlauf scheiterte. Beide Schritte sind wiederholbar: das Stelldatum
 * wird nur gesetzt, wo keines steht (Guard im UPDATE), der Empfaenger nur,
 * wo keiner steht. Noch nicht gestellte Fragen werden nach ihrer Katalog-
 * frist gruppiert (A 14 Tage, B/C 21 — `fristGruppen.ts`); bereits
 * gestellte bekommen keine neue Frist, auch nicht im zweiten Anlauf. Wirft
 * nie — der Fehler steht im Ergebnis.
 */
export const stempleKlaerungsliste = async (s: KlaerungslisteStempelAuftrag): Promise<KlaerungslisteStempel> => {
    const stempel: KlaerungslisteStempel = { fristen: [], empfaengerGesetzt: 0, fehler: null };
    try {
        const ungestellt = (s.fragen || []).filter(f => f && f.id && !f.gestellt_am);
        const gruppen = gruppiereNachFrist(ungestellt, fristTageFuerCode);
        stempel.fristen = await markiereGestelltJeFrist(gruppen, s.versendetAm);
        stempel.empfaengerGesetzt = await setzeFrageEmpfaengerWoLeer(
            (s.fragen || []).map(f => f.id).filter(Boolean),
            s.empfaenger.name,
            s.empfaenger.email,
        );
    } catch (e) {
        stempel.fehler = describeError(e);
    }
    return stempel;
};

export interface KlaerungslisteErgebnis {
    versand: VersandErgebnis;
    /** `null`, wenn der Hauptempfaenger nicht erreicht wurde — dann wird nichts gestempelt. */
    stempel: KlaerungslisteStempel | null;
}

/**
 * Klaerungsliste je Adressat: versenden, dann stempeln — nur, wenn die Mail
 * an den HAUPTEMPFAENGER hinausging (Kopien zaehlen nicht). Sonst bleibt
 * alles, wie es war; `klaerungslisteStempelMeldung` sagt es.
 */
export const versendeKlaerungsliste = async (
    a: VersandAuftrag,
    fragen: ReadonlyArray<Pick<Planungsfrage, 'id' | 'code' | 'gestellt_am'>>,
    optionen: VersandOptionen = {},
): Promise<KlaerungslisteErgebnis> => {
    const versand = await versende({ ...a, art: 'klaerungsliste' }, optionen);
    if (!darfStempeln(versand)) return { versand, stempel: null };
    const stempel = await stempleKlaerungsliste({
        fragen, versendetAm: versand.versendetAm, empfaenger: versand.hauptempfaenger,
    });
    return { versand, stempel };
};

/** Was der Dialog zum Stempel zeigt. `nachholbar` nur, wenn die Mail an den
 *  Hauptempfaenger hinausging und allein der Stempel fehlt. */
export interface StempelMeldung {
    text: string | null;
    fehler: string | null;
    nachholbar: boolean;
}

/**
 * Die Saetze zum Stempel fuer den Dialog: `text` bei Erfolg; `fehler`, wenn
 * der Hauptempfaenger nicht erreicht wurde (nichts gestempelt, nicht
 * nachholbar — erneut versenden) oder der Stempel scheiterte (nachholbar).
 * Nennt ausdruecklich, wenn nichts neu gestempelt wurde — das ERSTE
 * Stelldatum bleibt stehen (`stellMeldung`). Ging gar nichts hinaus, sagt
 * das der Versandstatus; hier dann nichts.
 */
export const klaerungslisteStempelMeldung = (ergebnis: KlaerungslisteErgebnis | null): StempelMeldung => {
    const nichts: StempelMeldung = { text: null, fehler: null, nachholbar: false };
    if (!ergebnis) return nichts;
    const { versand, stempel } = ergebnis;
    const nichtZugestellt = nichtZugestelltMeldung(versand);
    if (nichtZugestellt) {
        return { text: null, fehler: `${nichtZugestellt} Die Fragen gelten nicht als gestellt.`, nachholbar: false };
    }
    if (!stempel) return nichts;
    if (stempel.fehler) {
        return {
            text: null,
            fehler: 'Die E-Mail ist hinausgegangen, aber der Stempel scheiterte — die Fragen gelten noch nicht als gestellt: '
                + stempel.fehler,
            nachholbar: true,
        };
    }
    const fristen = stempel.fristen.reduce((n, f) => n + f.gesamt, 0)
        ? stellMeldung(stempel.fristen)
        : 'Keine Frage neu gestempelt — alle waren schon gestellt, das erste Stelldatum bleibt.';
    const empfaenger = stempel.empfaengerGesetzt
        ? ` ${stempel.empfaengerGesetzt} ${fragenWort(stempel.empfaengerGesetzt)} ${stempel.empfaengerGesetzt === 1 ? 'trägt' : 'tragen'} jetzt den Empfänger.`
        : ' An den Fragen stand bereits ein Empfänger — der erste bleibt.';
    return { text: `${fristen}${empfaenger}`, fehler: null, nachholbar: false };
};

// ═══════════════════════════════════════════════════════════════════════════
// BEDENKENANZEIGE — Mail raus → `versendet_am` (erstes Datum bleibt)
// ═══════════════════════════════════════════════════════════════════════════

export type BedenkenStempelStatus =
    | 'gestempelt'        // versendet_am jetzt gesetzt
    | 'schon_versendet'   // es stand schon ein Versanddatum — es bleibt (Guard im UPDATE)
    | 'nicht_versendet'   // Hauptempfaenger nicht erreicht (ggf. nur Kopien), nichts gestempelt
    | 'fehler';           // Mail raus, Stempel gescheitert — nachholen

export interface BedenkenStempel {
    status: BedenkenStempelStatus;
    /** Bei `nicht_versendet` immer `null` — es gibt keinen Zeitpunkt, der nachgeholt werden koennte. */
    versendetAm: string | null;
    /** Klartext bei `fehler`; bei `nicht_versendet` der Satz, wenn Kopien hinausgingen, sonst `null`. */
    fehler: string | null;
}

/** Stempelt den Versand eines Hinweises — auch zum Nachholen. Wirft nie. */
export const stempleBedenkenanzeige = async (hinweisId: string, versendetAm: string): Promise<BedenkenStempel> => {
    try {
        const neu = await markiereHinweisVersendet(hinweisId, versendetAm);
        return { status: neu ? 'gestempelt' : 'schon_versendet', versendetAm, fehler: null };
    } catch (e) {
        return { status: 'fehler', versendetAm, fehler: describeError(e) };
    }
};

export interface BedenkenanzeigeErgebnis {
    versand: VersandErgebnis;
    stempel: BedenkenStempel;
}

/**
 * Bedenkenanzeige (bzw. Hinweis, Gefahrenanzeige): versenden UND stempeln.
 * Der Aufrufer (HinweisEditor) baut nur das PDF und den Auftrag; ob die
 * Pflichtfelder stehen, hat er vorher mit `pruefeHinweis` geprueft. Der
 * Bauherr steht als ERSTER in der Liste — erreicht ihn die Mail nicht,
 * bleibt `versendet_am` unberuehrt, auch wenn Kopien hinausgingen: eine
 * Anzeige, die nur den Architekten erreicht hat, deckt keine Frage. Ein
 * zweiter Versand (Erinnerung) stempelt nicht neu: der Zugangsnachweis
 * haengt am ersten Datum.
 */
export const versendeBedenkenanzeige = async (
    hinweis: Pick<Planungshinweis, 'id'>,
    auftrag: VersandAuftrag,
    optionen: VersandOptionen = {},
): Promise<BedenkenanzeigeErgebnis> => {
    const versand = await versende({ ...auftrag, art: 'bedenkenanzeige' }, optionen);
    if (!darfStempeln(versand)) {
        const satz = nichtZugestelltMeldung(versand);
        return {
            versand,
            stempel: { status: 'nicht_versendet', versendetAm: null, fehler: satz ? `${satz} Die Anzeige gilt weiter als Entwurf.` : null },
        };
    }
    return { versand, stempel: await stempleBedenkenanzeige(hinweis.id, versand.versendetAm) };
};

/** Die Saetze zum Hinweis-Stempel fuer den Dialog. */
export const bedenkenStempelMeldung = (stempel: BedenkenStempel): { text: string | null; fehler: string | null } => {
    switch (stempel.status) {
        case 'gestempelt':
            return { text: `Als versendet festgehalten (${datumDeutsch(stempel.versendetAm) || 'heute'}). Die Anzeige deckt die genannten Fragen im Freigabe-Gate.`, fehler: null };
        case 'schon_versendet':
            return { text: 'Erneut versendet. Das erste Versanddatum bleibt stehen — für den Zugangsnachweis zählt der erste Versand.', fehler: null };
        case 'nicht_versendet':
            // Ging gar nichts hinaus, sagt das der Versandstatus; nur Kopien
            // erreicht → der Satz dazu, ohne „nachholen" (es gibt keinen Zeitpunkt).
            return { text: null, fehler: stempel.fehler };
        default:
            return {
                text: null,
                fehler: 'Die E-Mail ist hinausgegangen, aber der Stempel scheiterte — die Anzeige gilt noch als Entwurf: '
                    + (stempel.fehler || 'unbekannter Fehler'),
            };
    }
};
